import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { useId } from 'react';
import './forma.css';

/* ------------------------------------------------------------------- botao */

interface BotaoProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variante?: 'primario' | 'neutro' | 'silencioso' | 'perigo' | 'whatsapp';
  tamanho?: 'md' | 'sm';
  carregando?: boolean;
}

export function Botao({
  variante = 'neutro',
  tamanho = 'md',
  carregando = false,
  disabled,
  children,
  className = '',
  ...resto
}: BotaoProps) {
  return (
    <button
      className={`botao botao--${variante} botao--${tamanho} ${className}`}
      disabled={disabled || carregando}
      data-carregando={carregando || undefined}
      {...resto}
    >
      {children}
    </button>
  );
}

/* ------------------------------------------------------------------- campo */

interface CampoBase {
  rotulo: string;
  erro?: string;
  dica?: string;
  obrigatorio?: boolean;
  largura?: 'total' | 'meia' | 'terco' | 'curta';
}

function Envolucro({
  rotulo,
  erro,
  dica,
  obrigatorio,
  largura = 'total',
  id,
  children,
}: CampoBase & { id: string; children: ReactNode }) {
  return (
    <div className={`campo campo--${largura}`} data-erro={erro ? '' : undefined}>
      <label className="campo__rotulo rotulo" htmlFor={id}>
        {rotulo}
        {obrigatorio && (
          <span className="campo__obrigatorio" aria-hidden="true">
            *
          </span>
        )}
      </label>
      {children}
      {erro ? (
        <p className="campo__erro" role="alert">
          {erro}
        </p>
      ) : dica ? (
        <p className="campo__dica">{dica}</p>
      ) : null}
    </div>
  );
}

type CampoProps = CampoBase & Omit<InputHTMLAttributes<HTMLInputElement>, 'id'>;

export function Campo({ rotulo, erro, dica, obrigatorio, largura, ...resto }: CampoProps) {
  const id = useId();
  return (
    <Envolucro {...{ rotulo, erro, dica, obrigatorio, largura, id }}>
      <input
        id={id}
        className="entrada"
        aria-invalid={erro ? true : undefined}
        aria-required={obrigatorio}
        {...resto}
      />
    </Envolucro>
  );
}

type SelecaoProps = CampoBase & Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'>;

export function Selecao({ rotulo, erro, dica, obrigatorio, largura, children, ...resto }: SelecaoProps) {
  const id = useId();
  return (
    <Envolucro {...{ rotulo, erro, dica, obrigatorio, largura, id }}>
      <select id={id} className="entrada entrada--selecao" aria-invalid={erro ? true : undefined} {...resto}>
        {children}
      </select>
    </Envolucro>
  );
}

type AreaProps = CampoBase & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'>;

export function Area({ rotulo, erro, dica, obrigatorio, largura, ...resto }: AreaProps) {
  const id = useId();
  return (
    <Envolucro {...{ rotulo, erro, dica, obrigatorio, largura, id }}>
      <textarea id={id} className="entrada entrada--area" aria-invalid={erro ? true : undefined} {...resto} />
    </Envolucro>
  );
}

/* ------------------------------------------------------------------ avisos */

export function Aviso({ tipo = 'erro', children }: { tipo?: 'erro' | 'sucesso' | 'info'; children: ReactNode }) {
  return (
    <div className={`aviso aviso--${tipo}`} role={tipo === 'erro' ? 'alert' : 'status'}>
      {children}
    </div>
  );
}

/** Chip de estagio do exame. O mapeamento de cor vem do sistema atual. */
const ESTAGIOS = {
  AGUARDANDO_EXAME: 'Aguardando exame',
  AGUARDANDO_LAUDO: 'Aguardando laudo',
  RECUSADO: 'Recusado',
  FINALIZADO: 'Finalizado',
  CANCELADO: 'Cancelado',
} as const;

export type Estagio = keyof typeof ESTAGIOS;

export function Selo({ estagio }: { estagio: Estagio }) {
  return (
    <span className="selo" data-estagio={estagio}>
      {ESTAGIOS[estagio]}
    </span>
  );
}

/** Etiqueta do codigo do exame -- ver .chapa em base.css. */
export function Chapa({ codigo, estagio }: { codigo: number | string; estagio?: Estagio }) {
  return (
    <span className="chapa" data-estagio={estagio}>
      {codigo}
    </span>
  );
}
